import 'core-js';
import 'es6-promise/auto';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { isMobile } from 'react-device-detect';
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  PinterestShareButton,
  PinterestIcon,
  TwitterShareButton,
  TwitterIcon,
} from 'react-share';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { BANDS, bandByKey } from './bands';
import * as api from './api';
import { selectStyles } from './selectStyles';
import { parseX, findTokenToQuery, radioTokenToParams, buildFindLink, buildRadioLink } from './links';
import {
  itemsFromSet,
  segmentsToItems,
  makeSource,
  makeQueue,
  appendItems,
  needsMore,
  nextOtherChain,
  groupItems,
  isoFromDateKey,
  queuedSecs,
  fmtQueued,
} from './queue';
import { pushRecentSid } from './storage';
import {
  emptyQuery,
  normalizeQuery,
  isEmpty as isEmptyQuery,
  queryKeys,
  addSong,
  applyDrop,
} from './search/queryOps';
import { createRadioSource } from './vibe/radioSource';
import { SetSearchPanel, ActiveFilterChips, FindResultsHeader, FindResultRow, CountPill } from './search';
import { SurpriseMeButton, ShowInsights, VibeRadioPanel, RadioThumbs, VibeCheckBox, SongPage } from './vibe';
import PlayerProgress from './PlayerProgress';
import { partBadges } from './showPart';
import { isFillBand } from './fromTape';
import {
  IconPlay,
  IconPause,
  IconPrev,
  IconNext,
  IconBack10,
  IconFwd10,
  IconSearch,
  IconQueue,
  IconClose,
  IconVolume,
  IconMic,
  IconNote,
  IconStar,
  IconArrowDown,
  IconSun,
  IconMoon,
  IconRadio,
  IconPlusCircle,
  IconOpen,
  IconLink,
  IconList,
  IconChevronLeft,
  IconMore,
} from './icons';

import './App.css';

const errToast = (msg) => toast(msg, { type: toast.TYPE.ERROR });
// play() rejections the player ignores: AbortError (a newer load or pause
// superseded it) and NotSupportedError (a failed load; the 'error' listener
// skips the track, F5). Anything else (autoplay blocked) shows the play button.
const playRejectHandled = (err) => !!err && (err.name === 'AbortError' || err.name === 'NotSupportedError');
// Player load failures (F5): failed tracks in a row before playback stops,
// and one toast id so a run of skips shows a single toast.
const SKIP_CAP = 5;
const SKIP_TOAST_ID = 'track-skip';
// Stall watchdog (E4): a track whose server never answers fires no 'error',
// so while playing, an active element whose currentTime has not moved for
// STALL_MS without enough data to play (and no bytes arriving) counts as a
// failed track. Checked every STALL_TICK_MS.
const STALL_MS = 20000;
const STALL_TICK_MS = 1000;
const HAVE_FUTURE_DATA = 3;
const infoToast = (msg, autoClose = 2500) => toast(msg, { type: toast.TYPE.INFO, autoClose });

// Error copy (contract D.7). api.js puts the right string in err.userMessage;
// these are only the fallbacks for errors that did not come from api.js.
const errText = (err, fallback) => (err && err.userMessage) || fallback;
const FIND_ERROR = 'Could not run that search. Try again in a moment.';
const RADIO_ERROR = 'The radio could not load more songs.';
const LINK_ERROR = 'This search is too long to share as a link.';

// React 16 batches state updates only inside its own event handlers. Audio
// events, timers and promise callbacks go through this, so a queue change and
// its playNonce bump always land in one render (one run of the track effect).
const batch = (fn) => unstable_batchedUpdates(fn);

const FIND_PAGE = 50;
const EMPTY_CATALOG = { songs: [], venues: [], cities: [] };
const EMPTY_FIND = { total: 0, cursor: null, loading: false, loadingMore: false, dropOne: null, error: null };
const FETCHABLE = { matches: true, radio: true, song: true };

const fmtDate = (raw) => {
  if (!raw) return '';
  return moment(raw.substring(0, 10).replace(/_/g, '-'), 'YYYY-MM-DD').format('MM/DD/YYYY');
};

const fmtDateLong = (raw) => {
  if (!raw) return '';
  return moment(raw.substring(0, 10).replace(/_/g, '-'), 'YYYY-MM-DD').format('MMM D, YYYY');
};

const fmtCount = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

// Page-wide player keys (Space, ArrowLeft, ArrowRight) leave these alone:
// the control that has focus owns the key (a toggle, a tab list, a slider,
// a popover menu).
const OWN_KEYS =
  'input, textarea, select, button, a, [contenteditable="true"], [role="button"], [role="radio"], [role="tab"], ' +
  '[role="switch"], [role="menuitemradio"], [role="link"], .rc-slider, .ss-pop, .vb-pop';

// A mouse click on a play row or a transport button hands focus back to the
// page, so Space keeps meaning play and pause after the click. Keyboard
// activation (detail 0) keeps focus where it is.
const releaseMouseFocus = (e) => {
  if (e && e.detail > 0 && e.currentTarget && e.currentTarget.blur) e.currentTarget.blur();
};

// Year select while a search owns the Shows column: "Search results" is
// state, not a hint, so it gets readable text rather than the dim placeholder.
const yearSelectSearchStyles = {
  ...selectStyles,
  placeholder: (base, st) => ({ ...selectStyles.placeholder(base, st), color: 'var(--text-1)' }),
};

// 'A', 'A and B', 'A, B and C'
const joinNames = (names) =>
  names.length < 2 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];

// Browse show key for an ISO date: archive bands use the full archive date,
// WSP uses YYYY_MM_DD (contract B.0 dateKey).
const dateKeyFor = (band, iso) => (band.wsp ? iso.replace(/-/g, '_') : iso + 'T00:00:00Z');

const bandKeyOf = (b) => (typeof b === 'string' ? b : b && b.key) || null;

// Clipboard write with the textarea fallback. Resolves true when copied.
const copyText = (text) => {
  const fallback = () => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).then(
      () => true,
      () => fallback()
    );
  }
  return Promise.resolve(fallback());
};

// A toast with an Undo action (no native dialogs, contract rule 0.2).
const undoToast = (msg, onUndo) =>
  toast(
    ({ closeToast }) => (
      <span>
        {msg}{' '}
        <button
          className="more-link"
          onClick={() => {
            onUndo();
            closeToast();
          }}
        >
          Undo
        </button>
      </span>
    ),
    { type: toast.TYPE.INFO, autoClose: 6000 }
  );

// Share links keep the legacy ds.php format so existing links and the mobile
// app deep-link handler keep working.
const shareUrl = (queue) =>
  (
    'https://whsmacon.com/ds.php?set=' +
    queue.bandKey +
    '~' +
    queue.id +
    '~' +
    queue.date +
    '~' +
    queue.venue +
    '~' +
    queue.location +
    '~' +
    queue.bandName +
    ' - ' +
    queue.date.substring(0, 10) +
    ' - ' +
    queue.location
  )
    .replace(/\s/g, '%20')
    .replace(/&/g, '%26');

const SkeletonRows = ({ n = 8 }) => (
  <>
    {Array.from({ length: n }).map((_, i) => (
      <div className="skel-row" key={i} style={{ animationDelay: i * 60 + 'ms' }}>
        <div className="skel skel-line w60" />
        <div className="skel skel-line w90" />
        <div className="skel skel-line w40" />
      </div>
    ))}
  </>
);

const EmptyState = ({ icon, title, hint }) => (
  <div className="empty-state">
    <div className="empty-icon">{icon}</div>
    <div className="empty-title">{title}</div>
    {hint && <div className="empty-hint">{hint}</div>}
  </div>
);

const Eq = () => (
  <span className="eq" aria-hidden="true">
    <span />
    <span />
    <span />
  </span>
);

// Now Playing queue list. A radio or matches queue holds hundreds of rows, and
// App re-renders for many things that do not touch them (the spinner, the
// volume, panels), so each show group is a memo component: an append
// re-renders the list once, a track change re-renders the one or two groups
// that hold the old and the new current item, and nothing else does.
// onPick and onOpenShow must be stable functions. List keys are qids (D.2
// invariant 6).
const QueueGroup = React.memo(function QueueGroup({ g, activeIndex, playing, showHead, multiBand, onPick, onOpenShow }) {
  return (
    <div>
      {showHead && (
        <div className="row">
          <div className="row-main">
            <div className="row-title">
              {fmtDateLong(g.dateKey)}
              {multiBand ? ' · ' + g.bandName : ''}
            </div>
            <div className="row-sub dim">
              {g.venue}
              {g.location ? ' — ' + g.location : ''}
            </div>
          </div>
          <button
            className="icon-btn"
            title="Open show"
            onClick={() =>
              onOpenShow({ bandKey: g.bandKey, iso: g.iso, dateKey: g.dateKey, sid: g.sid, tapeId: g.tapeId })
            }
          >
            <IconOpen size={16} />
          </button>
        </div>
      )}
      {g.items.map(({ item, index }) => {
        const isCurrent = index === activeIndex;
        return (
          <button
            key={item.qid}
            className={'row track-row' + (isCurrent ? ' playing' : '')}
            onClick={(e) => {
              releaseMouseFocus(e);
              onPick(index);
            }}
          >
            <span className="track-no mono">{isCurrent && playing ? <Eq /> : item.no}</span>
            <span className="track-title">{item.title}</span>
            <span className="badge mono">{item.length}</span>
          </button>
        );
      })}
    </div>
  );
});

const QueueList = React.memo(function QueueList({ groups, index, playing, showHeads, multiBand, onPick, onOpenShow }) {
  return groups.map((g) => {
    const last = g.first + g.items.length - 1;
    const active = index >= g.first && index <= last;
    return (
      <QueueGroup
        key={g.key}
        g={g}
        activeIndex={active ? index : -1}
        playing={active && playing}
        showHead={showHeads}
        multiBand={multiBand}
        onPick={onPick}
        onOpenShow={onOpenShow}
      />
    );
  });
});

// Responsive layout. 'wide' (1024 px and up) is the three-column desktop
// layout; 'mid' (760-1023) folds the band list into a drawer and stacks
// Recordings above Setlist next to Shows; 'narrow' (under 760) is a one-pane
// drill-down, Shows then Recordings then Setlist. The mobile gate (isMobile)
// is separate: these widths serve desktop windows. App.css uses the same
// breakpoints through the app-compact / app-narrow classes.
const MQ_COMPACT = '(max-width: 1023px)';
const MQ_NARROW = '(max-width: 759px)';
const readLayout = () => {
  if (!window.matchMedia) return 'wide';
  if (window.matchMedia(MQ_NARROW).matches) return 'narrow';
  return window.matchMedia(MQ_COMPACT).matches ? 'mid' : 'wide';
};
function useLayout() {
  const [layout, setLayout] = useState(readLayout);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mqs = [MQ_COMPACT, MQ_NARROW].map((q) => window.matchMedia(q));
    const on = () => setLayout(readLayout());
    mqs.forEach((m) => (m.addEventListener ? m.addEventListener('change', on) : m.addListener(on)));
    on();
    return () => mqs.forEach((m) => (m.removeEventListener ? m.removeEventListener('change', on) : m.removeListener(on)));
  }, []);
  return layout;
}

// Narrow drill-down panes, in order.
const PANES = ['shows', 'tapes', 'set'];
const PANE_BACK_LABEL = { tapes: 'Shows', set: 'Recordings' };
// The pane shown (null outside the narrow layout). It needs its data: with
// no show selected it is Shows, with no tape selected at most Recordings.
const paneShown = (narrow, want, selShow, selTapeId) => {
  if (!narrow) return null;
  if (!selShow || want === 'shows') return 'shows';
  return want === 'set' && selTapeId ? 'set' : 'tapes';
};

// Small inline spinner next to the queue length while a page is on its way.
const QUEUE_SPINNER_STYLE = { display: 'inline-block', width: 10, height: 10, borderWidth: 2, marginLeft: 6, verticalAlign: '-1px' };

function MobileGate() {
  useEffect(() => {
    const t = setTimeout(() => {
      window.location = 'https://whsmacon.com';
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="mobile-gate">
      <span className="brand-text large">
        Dead<span>Spread</span>
      </span>
      <h1>Made for the big screen</h1>
      <p>
        DeadSpread on the web is designed for desktop. On mobile, the DeadSpread app is the way to
        go — you&rsquo;ll be redirected in a few seconds.
      </p>
      <a className="btn-primary" href="https://whsmacon.com">
        Get the App
      </a>
    </div>
  );
}

export default function App() {
  // ---- browse state ----
  const [band, setBand] = useState(BANDS[0]);
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(null);
  const [shows, setShows] = useState([]); // browse shows, or FindShow rows while a search is applied
  const [selShow, setSelShow] = useState(null); // canonical show object (or a FindShow row)
  const [tapes, setTapes] = useState([]);
  const [selTapeId, setSelTapeId] = useState(null);
  const [browseSet, setBrowseSet] = useState(null); // { tracks, setLength }
  const [bestTapeId, setBestTapeId] = useState(null);
  const [align, setAlign] = useState(null); // { tapeId, keys: { [trackIdx]: [songKey] } }

  // ---- Set Search state (contract D.4) ----
  const [meta, setMeta] = useState(null); // B.2 meta, null until loaded (or when search is off)
  const [catalog, setCatalog] = useState(EMPTY_CATALOG); // { songs, venues, cities }
  const [findDraft, setFindDraft] = useState(emptyQuery); // FindQuery being edited
  const [findApplied, setFindApplied] = useState(null); // FindQuery the Shows column shows, or null
  const [findResult, setFindResult] = useState(EMPTY_FIND);

  // ---- player / queue state (contract D.2) ----
  // queue = { gen, items: [QueueItem], index, title, source }
  const [queue, setQueue] = useState(null);
  const [queueFetching, setQueueFetching] = useState(0); // gen of the queue whose next page is on its way
  const [playNonce, setPlayNonce] = useState(0); // bumped by every explicit playAt
  const [playing, setPlaying] = useState(false);
  const [songSpinner, setSongSpinner] = useState(false);
  const [volume, setVolume] = useState(1);

  // ---- ui state ----
  const [loading, setLoading] = useState({ shows: true, tapes: false, set: false });
  const [openPanel, setOpenPanel] = useState(null); // null | 'queue' | 'search' | 'song' | 'radio' | 'bands' (the band drawer, under 1024 px)
  const [songView, setSongView] = useState(null); // { songKey, bandKey } for the SongPage panel
  const [expandedTapes, setExpandedTapes] = useState({});
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem('ds-theme');
      if (stored === 'dark' || stored === 'light') return stored;
    } catch (e) {
      /* private mode */
    }
    // first visit: start in whatever the OS prefers, then the toggle owns it
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });

  // ---- responsive layout ----
  const layout = useLayout();
  const compact = layout !== 'wide';
  const narrow = layout === 'narrow';
  const [paneWant, setPaneWant] = useState('shows'); // narrow drill-down: the pane the listener asked for
  const pane = paneShown(narrow, paneWant, selShow, selTapeId);
  const [menuOpen, setMenuOpen] = useState(false); // narrow header overflow menu

  const current = queue ? queue.items[queue.index] || null : null;
  const currentQid = current ? current.qid : null;

  // Request generations. Every async result checks the one it belongs to, so
  // a late answer for a band / year / search / selection the user already
  // left is dropped.
  const genRef = useRef(0); // Shows column content: band, year, search switches
  const bandGenRef = useRef(0); // bumped only by loadBand: years, meta, catalog
  const selGenRef = useRef(0); // show selection (tapes, best tape)
  const tapeGenRef = useRef(0); // tape selection (set, alignment)

  // Queue engine (contract D.2): gen of the live queue, batchNo for qids, the
  // gen whose fetch-more is in flight (0 = none), and invariant 5.
  const queueGenRef = useRef(0);
  const batchNoRef = useRef(0);
  const fetchingRef = useRef(0);
  const resumeOnAppendRef = useRef(0);
  const emptyPagesRef = useRef({ gen: 0, n: 0 });
  // The listener's latest play choice. Bumped by every new queue, a queue-row
  // pick and every async play request (claimPlayIntent); a response that
  // carries an older token is dropped, so a slow answer never overrides a
  // pick made after it. Auto-advance (playAt from ended / Next) leaves it.
  const playIntentRef = useRef(0);

  const pendingOpenRef = useRef(null); // openShow target waiting for a band or year load, stamped with its gen
  const scrollToPlayingRef = useRef(false);
  const scrollToShowRef = useRef(false);
  const setlistScrollRef = useRef(null);
  const showsScrollRef = useRef(null);
  const catalogPromiseRef = useRef(Promise.resolve(null));
  const lastSidRef = useRef(null);

  // Overlay panels: the element that opened one gets focus back on close.
  const queuePanelRef = useRef(null);
  const searchPanelRef = useRef(null);
  const songPanelRef = useRef(null);
  const panelOpenerRef = useRef(null);
  const prevPanelRef = useRef(null);
  const lastOutsideFocusRef = useRef(null); // last focused element outside the panels
  const sidebarRef = useRef(null); // the band list; a drawer below 1024 px
  const moreBtnRef = useRef(null);
  const menuRef = useRef(null);
  // Narrow panes: their sections, the move the listener made ('fwd' or
  // 'back': where focus goes once the pane changes) and the entry animation.
  const paneElsRef = useRef({});
  const paneMoveRef = useRef(null);
  const paneAnimRef = useRef('fwd');
  const paneRefCbsRef = useRef(null);
  if (!paneRefCbsRef.current) {
    paneRefCbsRef.current = {};
    PANES.forEach((k) => {
      paneRefCbsRef.current[k] = (el) => {
        paneElsRef.current[k] = el;
      };
    });
  }

  // Render-time mirrors. Audio listeners and the ticker are bound once at
  // mount, and async callbacks outlive their render: both read these refs,
  // never render-time state (D.2 invariant 3).
  const bandRef = useRef(band);
  bandRef.current = band;
  const yearRef = useRef(year);
  yearRef.current = year;
  const yearsRef = useRef(years);
  yearsRef.current = years;
  const showsRef = useRef(shows);
  showsRef.current = shows;
  const selShowRef = useRef(selShow);
  selShowRef.current = selShow;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const metaRef = useRef(meta);
  metaRef.current = meta;
  const findDraftRef = useRef(findDraft);
  findDraftRef.current = findDraft;
  const findAppliedRef = useRef(findApplied);
  findAppliedRef.current = findApplied;
  const findResultRef = useRef(findResult);
  findResultRef.current = findResult;
  const queueRef = useRef(null);
  queueRef.current = queue;
  const currentRef = useRef(null);
  currentRef.current = current;
  const playingRef = useRef(false);
  playingRef.current = playing;
  const songSpinnerRef = useRef(false);
  songSpinnerRef.current = songSpinner;
  const volumeRef = useRef(1);
  volumeRef.current = volume;
  const openPanelRef = useRef(null);
  openPanelRef.current = openPanel;
  const paneRef = useRef(pane);
  paneRef.current = pane;

  // Gapless engine: two <audio> elements. The active one plays; the standby
  // one preloads the next track in the queue, so on 'ended' we hot-swap to an
  // already-buffered element instead of reloading a single player.
  const audiosRef = useRef(null);
  if (!audiosRef.current) {
    audiosRef.current = [new Audio(), new Audio()];
    audiosRef.current.forEach((a) => {
      a.preload = 'auto';
    });
  }
  const activeIdxRef = useRef(0);
  const srcsRef = useRef(['', '']); // what we assigned (el.src normalizes URLs)
  // Load failures (F5). standbyFailedRef: the url the standby element failed
  // to preload; it is never handed off to (the track effect loads it into the
  // active element once more instead), and srcsRef still names it, so
  // preloadNext does not re-request it. failRunRef: failed tracks in a row
  // under one playIntent; a track that plays or a new pick resets it.
  const standbyFailedRef = useRef('');
  const failRunRef = useRef({ intent: 0, n: 0 });
  // Stall watchdog (E4). stallRef: what the last check saw on the active
  // element and when it last moved (currentTime, a new element or url, bytes
  // arriving, a seek). stallMarkRef[i]: the url element i stalled on; cleared
  // by that element's next loadstart. A stalled element is treated like one
  // with an error: never handed off to, reloaded when played again.
  const stallRef = useRef({ el: null, src: '', t: -1, since: 0 });
  const stallMarkRef = useRef(['', '']);
  const stalledAt = (i) => !!stallMarkRef.current[i] && stallMarkRef.current[i] === srcsRef.current[i];
  const stallTouch = () => {
    stallRef.current.since = Date.now();
  };
  // Mid-track stalls (E4 follow-up): a track that has played and then stops
  // getting bytes (a dropped connection) is reloaded once where it stopped
  // before it counts as failed. playedRef: the element and url that last
  // fired 'playing'. stallRetryRef: the {el, src} already reloaded once
  // (cleared by the track effect).
  const playedRef = useRef({ el: null, src: '' });
  const stallRetryRef = useRef(null);
  const activeEl = () => audiosRef.current[activeIdxRef.current];
  // Stable for PlayerProgress (a memo component): same function every render.
  const getActiveElRef = useRef(() => audiosRef.current[activeIdxRef.current]);

  const setLoad = (patch) => setLoading((l) => ({ ...l, ...patch }));

  // Search is usable for the current band: meta loaded and not 'off'.
  const searchUsable = () => !!(metaRef.current && metaRef.current.mode !== 'off');

  const songByKey = useMemo(() => {
    const m = {};
    (catalog.songs || []).forEach((s) => {
      m[s.key] = s;
    });
    return m;
  }, [catalog]);

  const matchKeys = useMemo(() => {
    if (!findApplied) return null;
    const m = {};
    queryKeys(findApplied).forEach((k) => {
      m[k] = true;
    });
    return m;
  }, [findApplied]);

  // ---- data loading ----------------------------------------------------

  // Narrow drill-down. goPane records the move so focus follows it (forward:
  // the new pane; back: the row selected there, at its old scroll position,
  // since hidden panes keep their layout).
  const goPane = (p, dir = 'fwd') => {
    if (paneRef.current && p !== paneRef.current) {
      paneMoveRef.current = dir;
      paneAnimRef.current = dir;
    }
    setPaneWant(p);
  };
  const paneBack = () => {
    const i = PANES.indexOf(paneRef.current);
    if (i > 0) goPane(PANES[i - 1], 'back');
  };

  const resetSelection = () => {
    setPaneWant('shows');
    setSelShow(null);
    setTapes([]);
    setSelTapeId(null);
    setBrowseSet(null);
    setBestTapeId(null);
    setAlign(null);
  };

  // A browse or find row that is the openShow target: by sid for find rows,
  // then by dateKey, then by ISO date (early and late shows share a date).
  const matchShow = (list, t) => {
    const rows = list || [];
    if (t.sid) {
      const bySid = rows.find((s) => s.sid && s.sid === t.sid);
      if (bySid) return bySid;
    }
    return rows.find((s) => s.dateKey === t.dateKey) || rows.find((s) => isoFromDateKey(s.dateKey) === t.iso) || null;
  };

  const consumePendingOpen = (b, list) => {
    const t = pendingOpenRef.current;
    if (!t) return;
    pendingOpenRef.current = null;
    // Stamped with the Shows-column gen it was waiting for: a band or year
    // change since then means the listener moved on, so it never fires late.
    if (t.gen !== genRef.current || t.bandKey !== b.key) return;
    // not listed (for example a date the browse list folded): open it anyway
    const show = matchShow(list, t) || {
      dateKey: t.dateKey,
      dateApi: t.dateKey,
      displayDate: t.iso,
      venue: '',
      location: '',
      length: '',
      sid: t.sid || undefined,
    };
    scrollToShowRef.current = true;
    selectShow(show, { tapeId: t.tapeId, scroll: true });
  };

  const loadShows = (b, y, gen) => {
    setLoad({ shows: true });
    api
      .fetchShows(b, y)
      .then((data) => {
        if (gen !== genRef.current) return;
        batch(() => {
          setShows(data);
          setLoad({ shows: false });
        });
        consumePendingOpen(b, data);
      })
      .catch(() => {
        if (gen !== genRef.current) return;
        setLoad({ shows: false });
        errToast('Error retrieving show metadata.');
      });
  };

  // Band switch. `opts.year` opens that year instead of the band's first one,
  // so openShow never lands on (and races) the wrong year (contract D.2).
  const loadBand = (b, opts = {}) => {
    const gen = ++genRef.current;
    const bg = ++bandGenRef.current;
    bandRef.current = b;
    batch(() => {
      setBand(b);
      setYears([]);
      setYear(null);
      setShows([]);
      resetSelection();
      setMeta(null);
      setCatalog(EMPTY_CATALOG);
      setFindDraft(emptyQuery());
      setFindApplied(null);
      setFindResult(EMPTY_FIND);
      setOpenPanel((p) => (p === 'search' || p === 'radio' ? null : p));
      setLoad({ shows: true, tapes: false, set: false });
    });

    api
      .fetchYears(b)
      .then((ys) => {
        if (bg !== bandGenRef.current) return;
        const want = opts.year != null ? String(opts.year) : null;
        const y = (want && ys.find((v) => String(v) === want)) || ys[0];
        batch(() => {
          setYears(ys.map((v) => ({ value: v, label: v })));
          setYear(y);
        });
        // a search (x= link) may already own the Shows column
        if (gen === genRef.current || !findAppliedRef.current) loadShows(b, y, genRef.current);
      })
      .catch(() => {
        if (bg !== bandGenRef.current) return;
        setLoad({ shows: false });
        errToast('Error retrieving show years.');
      });

    // Search availability comes from the server (GET /ds/catalog/meta), not a
    // per-band flag. On any failure the search button stays hidden.
    catalogPromiseRef.current = api
      .fetchMeta(b)
      .then((m) => {
        if (bg !== bandGenRef.current) return null;
        if (!m || m.mode === 'off') {
          setMeta(m || null);
          return null;
        }
        setMeta(m);
        return Promise.all([api.fetchCatalogSongs(b), api.fetchVenues(b), api.fetchCities(b)]).then(
          ([songs, venues, cities]) => {
            if (bg !== bandGenRef.current) return null;
            const c = { songs, venues, cities };
            setCatalog(c);
            return c;
          }
        );
      })
      .catch((err) => {
        if (bg !== bandGenRef.current) return null;
        setMeta(null);
        errToast(errText(err, 'Could not load the song list for ' + b.name + '. Search is off for now.'));
        return null;
      });
  };

  // Year switch. An applied Set Search is cleared, with an Undo that brings
  // it back (contract D.4 changeYear).
  const switchYear = (y) => {
    const prev = findAppliedRef.current;
    const gen = ++genRef.current;
    batch(() => {
      setYear(y);
      setShows([]);
      resetSelection();
      if (prev) {
        setFindApplied(null);
        setFindResult(EMPTY_FIND);
      }
    });
    if (prev) {
      const bk = bandRef.current.key;
      undoToast('Set Search cleared.', () => {
        // the search belongs to the band it ran on: after a band switch the
        // Undo would run it on the wrong catalog, so it does nothing
        if (bandRef.current.key !== bk) return;
        setFindDraft(prev);
        runFind(prev);
      });
    }
    loadShows(bandRef.current, y, gen);
  };

  // Year select (the listener's own pick): a pending Open show is dropped.
  const changeYear = (opt) => {
    if (!opt) return;
    pendingOpenRef.current = null;
    switchYear(opt.value);
  };

  const pickBestTape = (detailShows) => {
    const s = (detailShows || [])[0];
    if (!s) return null;
    const t = (s.tapes || []).find((x) => x.best) || s.bestTape;
    return t && t.id ? t.id : null;
  };

  const selectShow = (show, opts = {}) => {
    const b = bandRef.current;
    const gen = genRef.current;
    const sg = ++selGenRef.current;
    ++tapeGenRef.current; // drop a set / alignment still loading for the previous show
    batch(() => {
      setSelShow(show);
      setTapes([]);
      setSelTapeId(null);
      setBrowseSet(null);
      setAlign(null);
      setBestTapeId(show.bestTape && show.bestTape.id ? show.bestTape.id : null);
      setLoad({ tapes: true, set: false });
    });
    api
      .fetchTapes(b, show.dateApi)
      .then((data) => {
        if (gen !== genRef.current || sg !== selGenRef.current) return;
        batch(() => {
          setTapes(data);
          setLoad({ tapes: false });
        });
        if (opts.tapeId) {
          const t = data.find((x) => x.id === opts.tapeId);
          if (t) selectTape(t, { show, scroll: opts.scroll });
        }
      })
      .catch(() => {
        if (gen !== genRef.current || sg !== selGenRef.current) return;
        setLoad({ tapes: false });
        errToast('Error retrieving tapes metadata.');
      });
    // Browsing: the best tape comes from the show detail (find rows carry it).
    if (!show.bestTape && searchUsable()) {
      api
        .fetchShowDetail(b, { date: show.iso || isoFromDateKey(show.dateKey) })
        .then((r) => {
          if (sg !== selGenRef.current) return;
          const best = pickBestTape(r.shows);
          if (best) setBestTapeId(best);
        })
        .catch(() => {
          /* the Best pill is an extra; browsing works without it */
        });
    }
  };

  const selectTape = (tape, opts = {}) => {
    const b = bandRef.current;
    const show = opts.show || selShowRef.current;
    const gen = genRef.current;
    const tg = ++tapeGenRef.current;
    batch(() => {
      setSelTapeId(tape.id);
      setBrowseSet(null);
      setAlign(null);
      setLoad({ set: true });
    });
    scrollToPlayingRef.current = !!opts.scroll;
    api
      .fetchSet(b, tape.id)
      .then((data) => {
        if (gen !== genRef.current || tg !== tapeGenRef.current) return;
        batch(() => {
          setBrowseSet(data);
          setLoad({ set: false });
        });
        // Nothing playing yet? Warm the opener into the standby element while
        // the user reads the setlist, so the first press of play is instant.
        if (!currentRef.current && data.tracks.length) {
          const sb = 1 - activeIdxRef.current;
          if (srcsRef.current[sb] !== data.tracks[0].url) {
            audiosRef.current[sb].src = data.tracks[0].url;
            srcsRef.current[sb] = data.tracks[0].url;
            standbyFailedRef.current = '';
            audiosRef.current[sb].load();
          }
        }
      })
      .catch(() => {
        if (gen !== genRef.current || tg !== tapeGenRef.current) return;
        setLoad({ set: false });
        errToast('Error retrieving set metadata.');
      });
    // Song keys per track: match highlighting, Add to Set Search, song pages.
    if (searchUsable()) {
      api
        .fetchAlign(b, tape.id, show && show.sid ? show.sid : undefined)
        .then((r) => {
          if (tg !== tapeGenRef.current) return;
          const keys = {};
          (r.tracks || []).forEach((t) => {
            keys[t.trackIdx] = Array.isArray(t.songKeys) ? t.songKeys : [];
          });
          setAlign({ tapeId: tape.id, keys });
        })
        .catch(() => {
          /* no alignment: rows just lose the match and add controls */
        });
    }
  };

  // ---- Set Search (contract D.4) ---------------------------------------

  // Drop chips whose keys the server no longer knows (find.unknownKeys).
  const dropUnknownKeys = (q, keys) =>
    keys.reduce((acc, k) => {
      let next = applyDrop(acc, { kind: 'song', value: k });
      next = applyDrop(next, { kind: 'not', value: k });
      next.sandwiches
        .filter((w) => w.outer.key === k || w.inner.key === k)
        .forEach((w) => {
          next = applyDrop(next, { kind: 'sandwich', value: w.outer.key + '~' + w.inner.key });
        });
      return next;
    }, q);

  const clearFind = () => {
    const gen = ++genRef.current;
    batch(() => {
      setFindApplied(null);
      setFindDraft(emptyQuery());
      setFindResult(EMPTY_FIND);
      setShows([]);
      resetSelection();
    });
    if (yearRef.current != null) loadShows(bandRef.current, yearRef.current, gen);
  };

  const runFind = (q, b = bandRef.current, opts = {}) => {
    if (!q || isEmptyQuery(q)) {
      clearFind();
      return;
    }
    const gen = ++genRef.current;
    batch(() => {
      setFindApplied(q);
      setShows([]);
      resetSelection();
      setFindResult({ ...EMPTY_FIND, loading: true });
      setLoad({ shows: true });
      setOpenPanel((p) => (p === 'search' ? null : p));
    });
    api
      .find(b, q, { limit: FIND_PAGE })
      .then((res) => {
        if (gen !== genRef.current) return;
        if (res.unknownKeys.length && !opts.retried) {
          const cleaned = dropUnknownKeys(q, res.unknownKeys);
          if (JSON.stringify(normalizeQuery(cleaned)) !== JSON.stringify(normalizeQuery(q))) {
            const n = res.unknownKeys.length;
            infoToast(n === 1 ? '1 song is no longer in the catalog.' : n + ' songs are no longer in the catalog.', 4000);
            setFindDraft(cleaned);
            runFind(cleaned, b, { retried: true });
            return;
          }
        }
        batch(() => {
          setShows(res.rows);
          setFindResult({ ...EMPTY_FIND, total: res.total, cursor: res.cursor });
          setLoad({ shows: false });
        });
        // zero results: fetch the drop-one suggestions for CountPill
        if (!res.rows.length) {
          api
            .findCount(b, q)
            .then((c) => {
              if (gen === genRef.current) setFindResult((r) => ({ ...r, dropOne: c.dropOne }));
            })
            .catch(() => {
              /* suggestions are optional */
            });
        }
      })
      .catch((err) => {
        if (gen !== genRef.current) return;
        batch(() => {
          setFindResult({ ...EMPTY_FIND, error: errText(err, FIND_ERROR) });
          setLoad({ shows: false });
        });
      });
  };

  const loadMoreFind = () => {
    const q = findAppliedRef.current;
    const fr = findResultRef.current;
    if (!q || !fr.cursor || fr.loadingMore) return;
    const gen = genRef.current;
    const b = bandRef.current;
    setFindResult((r) => ({ ...r, loadingMore: true }));
    api
      .find(b, q, { limit: FIND_PAGE, cursor: fr.cursor })
      .then((res) => {
        if (gen !== genRef.current) return;
        batch(() => {
          setShows((list) => {
            const seen = {};
            list.forEach((s) => {
              if (s.sid) seen[s.sid] = true;
            });
            return list.concat(res.rows.filter((r) => !r.sid || !seen[r.sid]));
          });
          setFindResult((r) => ({ ...r, total: res.total, cursor: res.cursor, loadingMore: false }));
        });
      })
      .catch((err) => {
        if (gen !== genRef.current) return;
        setFindResult((r) => ({ ...r, loadingMore: false }));
        errToast(errText(err, FIND_ERROR));
      });
  };

  // VibeCheckBox and SetSearchPanel: set the draft; run it when asked.
  const onApplyQuery = (nextQuery, { run } = {}) => {
    setFindDraft(nextQuery);
    if (run) runFind(nextQuery);
  };

  // ActiveFilterChips: a chip was removed.
  const onActiveFilterChange = (nextQuery) => {
    if (!nextQuery || isEmptyQuery(nextQuery)) {
      clearFind();
      return;
    }
    setFindDraft(nextQuery);
    runFind(nextQuery);
  };

  // CountPill: apply one B.3 drop-one suggestion.
  const onDropOne = (entry) => {
    const q = findAppliedRef.current;
    if (!q) return;
    const next = applyDrop(q, entry);
    setFindDraft(next);
    runFind(next);
  };

  const onFindSort = (sort) => {
    const q = findAppliedRef.current;
    if (!q || !sort) return;
    const next = { ...q, sort };
    setFindDraft((d) => ({ ...d, sort }));
    runFind(next);
  };

  const onShareFind = () => {
    const q = findAppliedRef.current;
    const link = q ? buildFindLink(bandRef.current.key, q) : null;
    if (!link) {
      errToast(LINK_ERROR);
      return;
    }
    copyText(link).then((ok) => (ok ? infoToast('Search link copied', 2000) : errToast('Could not copy the link.')));
  };

  // Append songs to the draft and open the Set Search panel.
  const addToSearch = (songs) => {
    const list = (songs || []).filter((s) => s && s.key);
    if (!list.length) return;
    const draft = findDraftRef.current;
    const had = {};
    draft.songs.forEach((s) => {
      had[s.key] = true;
    });
    const next = list.reduce((acc, s) => addSong(acc, s), draft);
    const added = list.filter((s) => !had[s.key] && next.songs.some((c) => c.key === s.key));
    setFindDraft(next);
    setOpenPanel('search');
    if (added.length) infoToast('Added ' + joinNames(added.map((s) => s.name)) + ' to Set Search');
    else if (list.every((s) => had[s.key]))
      infoToast(joinNames(list.map((s) => s.name)) + (list.length > 1 ? ' are' : ' is') + ' already in Set Search');
    else infoToast('Set Search holds up to 12 songs.');
  };

  const songName = (key) => (songByKey[key] && songByKey[key].name) || key;

  // D.4 callback: onAddToSearch({ key, name })
  const onAddToSearch = ({ key, name } = {}) => {
    if (key) addToSearch([{ key, name: name || songName(key) }]);
  };

  // D.4 callback: onOpenSong(songKey, bandKey = band.key)
  const onOpenSong = (songKey, bandKey) => {
    if (!songKey) return;
    setSongView({ songKey, bandKey: bandKey || bandRef.current.key });
    setOpenPanel('song');
  };

  // ---- playback --------------------------------------------------------

  // Start a new queue. `gen` increments on every new queue, so fetch-more
  // results for an older queue are dropped (D.2 invariant 2).
  const setNewQueue = (items, title, source, index, { play = true } = {}) => {
    const gen = ++queueGenRef.current;
    resumeOnAppendRef.current = 0;
    playIntentRef.current += 1;
    const q = makeQueue(gen, items, title, source, index);
    batch(() => {
      setQueue(q);
      setPlayNonce((n) => n + 1);
      setPlaying(play);
      setSongSpinner(play);
    });
    return gen;
  };

  // Play item `index` of queue `q`. When `q` is the live queue, the update
  // keeps any items appended since the caller read it. An explicit pick also
  // cancels a pending tail resume (D.2 invariant 5): the page that lands
  // later is appended, and playback stays on the pick.
  const playAt = (q, index) => {
    if (!q || index < 0 || index >= q.items.length) return;
    resumeOnAppendRef.current = 0;
    batch(() => {
      setQueue((prev) => (prev && prev.gen === q.gen ? { ...prev, index } : { ...q, index }));
      setPlayNonce((n) => n + 1);
      setPlaying(true);
      setSongSpinner(true);
    });
  };

  // Token for an async play request (Surprise, Play the Matches, Radio, the
  // SongPage and ShowInsights play buttons). Passed back as
  // onPlaySegments(..., { intent }); a stale token is dropped.
  const claimPlayIntent = () => {
    playIntentRef.current += 1;
    return playIntentRef.current;
  };

  // Play a track out of the browse columns: the browsed tape becomes the queue.
  const playFromBrowse = (trackIdx) => {
    if (!browseSet || !selShow) return;
    const items = itemsFromSet(band, selShow, selTapeId, browseSet);
    setNewQueue(items, band.name, makeSource('show', band, { setLength: browseSet.setLength }), trackIdx);
  };

  // D.2 onPlaySegments(segments, source, { title }): Play the Matches,
  // Surprise, Radio, Song Radio and every Play button in the vibe components.
  // Additive: `intent` from claimPlayIntent(). An answer that arrives after
  // the listener picked something else is dropped (returns false).
  const onPlaySegments = (segments, source, opts) => {
    if (opts && opts.intent != null && opts.intent !== playIntentRef.current) return false;
    const items = segmentsToItems(segments, ++batchNoRef.current);
    if (!items.length) {
      errToast('Nothing here can be played right now.');
      return false;
    }
    const s = {};
    Object.keys(source || {}).forEach((k) => {
      if (source[k] !== undefined) s[k] = source[k];
    });
    const src = makeSource(s.kind || 'show', s.band || bandRef.current, s);
    setNewQueue(items, (opts && opts.title) || '', src, 0);
    // Surprise Me keeps its own card (Reroll, Open show) in view; everything
    // else opens Now Playing.
    if (src.kind !== 'surprise') setOpenPanel('queue');
    return true;
  };

  const canFetch = (q) => !!(q && q.source && FETCHABLE[q.source.kind] && !q.source.exhausted);

  // Fetch-more (D.2 dispatch table). Appends go through appendItems with the
  // queue's gen; existing items are never rebuilt (invariant 2).
  const fetchMore = (q) => {
    const src = q && q.source;
    if (!src || fetchingRef.current === q.gen) return;
    const gen = q.gen;
    let p;
    // Long pages (RADIO2): MATCHES_PAGE shows, RADIO_PAGE_MINUTES of radio.
    // `pages` counts the pages this queue has; needsMore's first fill uses it.
    const pages = (src.pages || 1) + 1;
    if (src.kind === 'matches') {
      p = api.findMatches(src.band, src.params, { cursor: src.cursor, limit: api.MATCHES_PAGE }).then((r) => ({
        segments: r.segments,
        patch: { cursor: r.cursor, exhausted: !r.cursor || r.cursor === src.cursor, pages },
      }));
    } else if (src.kind === 'radio' && src.radio) {
      const radio = src.radio;
      p = radio.more().then((segs) => ({ segments: segs, patch: { exhausted: radio.exhausted, pages } }));
    } else if (src.kind === 'song') {
      p = api.radio(src.band, { ...src.params, minutes: api.RADIO_PAGE_MINUTES }, src.cursor).then((r) => ({
        segments: r.segments || [],
        patch: { cursor: r.cursor || null, exhausted: !!r.exhausted || !r.cursor || r.cursor === src.cursor, pages },
      }));
    } else {
      return;
    }
    fetchingRef.current = gen;
    setQueueFetching(gen);
    const doneFetching = () => setQueueFetching((g) => (g === gen ? 0 : g));
    p.then(({ segments, patch }) => {
      if (fetchingRef.current === gen) fetchingRef.current = 0;
      doneFetching();
      const cq = queueRef.current;
      if (!cq || cq.gen !== gen) return;
      const items = segmentsToItems(segments, ++batchNoRef.current);
      // pages that add nothing three times running end the source
      const ep = emptyPagesRef.current;
      if (ep.gen !== gen) emptyPagesRef.current = { gen, n: 0 };
      emptyPagesRef.current.n = items.length ? 0 : emptyPagesRef.current.n + 1;
      const finalPatch = emptyPagesRef.current.n >= 3 ? { ...patch, exhausted: true } : patch;
      const resume = resumeOnAppendRef.current === gen;
      if (resume) resumeOnAppendRef.current = 0;
      if (resume && items.length) {
        // invariant 5: the tail ended while this page was on its way. Move to
        // the first new item; if the listener paused while waiting, load it
        // paused, so Play starts it instead of replaying the finished track.
        const keepPlaying = playingRef.current;
        batch(() => {
          setQueue((prev) => {
            const nq = appendItems(prev, gen, items, finalPatch);
            return nq === prev ? prev : { ...nq, index: prev.items.length };
          });
          setPlayNonce((n) => n + 1);
          setSongSpinner(keepPlaying);
          if (!keepPlaying) setPlaying(false);
        });
      } else {
        batch(() => {
          setQueue((prev) => appendItems(prev, gen, items, finalPatch));
          if (resume) {
            setPlaying(false);
            setSongSpinner(false);
          }
        });
      }
    }).catch((err) => {
      if (fetchingRef.current === gen) fetchingRef.current = 0;
      doneFetching();
      const cq = queueRef.current;
      if (!cq || cq.gen !== gen) return;
      errToast(errText(err, src.kind === 'matches' ? FIND_ERROR : RADIO_ERROR));
      if (resumeOnAppendRef.current === gen) {
        resumeOnAppendRef.current = 0;
        batch(() => {
          setPlaying(false);
          setSongSpinner(false);
        });
      }
    });
  };

  // Index-based stepping: disc-numbered tapes (112 -> 201) just work.
  const stepTrack = (dir) => {
    const q = queueRef.current;
    if (!q || !q.items.length) return false;
    const i = q.index + dir;
    if (i < 0 || i >= q.items.length) return false;
    playAt(q, i);
    return true;
  };

  const prevTrack = () => {
    if (!stepTrack(-1)) activeEl().currentTime = 0;
  };

  const nextTrack = () => stepTrack(1);

  // Wait for the next page, then play its first item (D.2 invariant 5).
  const resumeAfterFetch = (q) => {
    resumeOnAppendRef.current = q.gen;
    setSongSpinner(true);
    if (fetchingRef.current !== q.gen) fetchMore(q);
  };

  const fastBackward = () => {
    const el = activeEl();
    el.currentTime = el.currentTime < 10 ? 0 : el.currentTime - 10;
  };

  const fastForward = () => {
    const el = activeEl();
    if (!el.duration || el.duration - el.currentTime > 10) {
      el.currentTime = el.currentTime + 10;
    } else {
      nextTrack();
    }
  };

  // PlayerProgress ticks (100 ms, only while the active element plays). App
  // state changes only when the spinner flips, so App no longer re-renders
  // ten times a second. Fallback preload trigger on slow links where
  // canplaythrough is late; it also picks up items appended after
  // canplaythrough already fired (D.2 invariant 4).
  const onProgressTick = (el) => {
    if (!currentRef.current || el !== activeEl()) return;
    if (songSpinnerRef.current) setSongSpinner(false);
    if (el.currentTime > 15) preloadNext();
  };
  const onProgressTickRef = useRef(onProgressTick);
  onProgressTickRef.current = onProgressTick;
  const progressTickRef = useRef((el) => onProgressTickRef.current(el));

  // RadioThumbs onVote(dir): feedback goes into the station; a thumbs down
  // also skips the rest of that chain (D.4.2). The standby preload then
  // mismatches and the track effect aborts it.
  const onRadioVote = (dir) => {
    const q = queueRef.current;
    const cur = currentRef.current;
    if (!q || !cur || q.source.kind !== 'radio' || !q.source.radio) return;
    const { skipChainId } = q.source.radio.vote(cur, dir);
    if (dir > 0) {
      infoToast('More like this', 1500);
      return;
    }
    infoToast('Less like this', 1500);
    const i = nextOtherChain(q.items, q.index, skipChainId);
    if (i >= 0) playAt(q, i);
    else if (fetchingRef.current === q.gen || canFetch(q)) resumeAfterFetch(q);
  };

  // D.4.2 VibeRadioPanel onStart(radioParams, label). The RadioSource lives in
  // queue.source.radio; fetch-more calls its more().
  const onStartRadio = (radioParams, label) => {
    const b = bandRef.current;
    const intent = claimPlayIntent();
    const radio = createRadioSource(b, radioParams);
    return radio
      .start()
      .then((segs) => {
        const l = radio.label();
        onPlaySegments(
          segs,
          { kind: 'radio', band: b, params: radio.params(), radio, exhausted: radio.exhausted },
          { title: l === 'Radio' && label ? label : l, intent }
        );
      })
      .catch((err) => {
        if (intent === playIntentRef.current) errToast(errText(err, RADIO_ERROR));
      });
  };

  // D.2 openShow: switch band (at the show's year) if needed, find the row,
  // select it and its tape, scroll to the playing track. Never touches
  // playback: selectTape only warms the standby when nothing is playing.
  const onOpenShow = ({ bandKey, iso, dateKey, sid, tapeId } = {}) => {
    const b = bandByKey(bandKey) || bandRef.current;
    const isoD = iso || isoFromDateKey(dateKey);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(isoD)) return;
    const t = {
      bandKey: b.key,
      iso: isoD,
      dateKey: dateKey || dateKeyFor(b, isoD),
      sid: sid || null,
      tapeId: tapeId || null,
    };
    setOpenPanel(null);
    const y = isoD.slice(0, 4);
    // narrow layout: land on the setlist (Recordings until the tape is in)
    const landOnSet = () => {
      paneAnimRef.current = 'fwd';
      setPaneWant(t.tapeId ? 'set' : 'tapes');
    };
    if (b.key !== bandRef.current.key) {
      pendingOpenRef.current = t;
      loadBand(b, { year: y });
      t.gen = genRef.current; // the Shows load this target waits for
      landOnSet();
      return;
    }
    const listed = loadingRef.current.shows ? null : matchShow(showsRef.current, t);
    if (listed) {
      pendingOpenRef.current = null;
      scrollToShowRef.current = true;
      selectShow(listed, { tapeId: t.tapeId, scroll: true });
      landOnSet();
      return;
    }
    pendingOpenRef.current = t;
    const yv = (yearsRef.current.find((o) => String(o.value) === y) || { value: y }).value;
    switchYear(yv);
    t.gen = genRef.current;
    landOnSet();
  };

  const openCurrentShow = () => {
    const cur = currentRef.current;
    if (!cur) return;
    onOpenShow({ bandKey: cur.bandKey, iso: cur.iso, dateKey: cur.dateKey, sid: cur.sid, tapeId: cur.tapeId });
  };

  // Deep links: /deadspread/?set=band~id~date~venue~location~name — same
  // format ds.php and the mobile app have always used.
  const linkSet = (parts) => {
    const b = bandByKey(parts[0]);
    if (!b) return;
    const [, setid, date, venue, location] = parts;
    api
      .fetchSet(b, setid)
      .then((data) => {
        if (!data.tracks.length) return;
        const items = itemsFromSet(b, { dateKey: date, venue, location }, setid, data);
        setNewQueue(items, b.name, makeSource('link', b, { setLength: data.setLength }), 0);
        setOpenPanel('queue');
      })
      .catch(() => errToast('Error retrieving set metadata.'));
  };

  // Deep links: /deadspread/?x=find.1.<token> and ?x=radio.1.<token> (D.5).
  const openXLink = (x, b) => {
    const bg = bandGenRef.current;
    if (x.kind === 'find') {
      catalogPromiseRef.current.then((cat) => {
        if (bg !== bandGenRef.current || !cat) return;
        const q = findTokenToQuery(x.data, cat);
        if (isEmptyQuery(q)) return;
        setFindDraft(q);
        runFind(q, b);
      });
    } else if (x.kind === 'radio') {
      const params = radioTokenToParams(x.data);
      if (!params) return;
      const radio = createRadioSource(b, params);
      radio
        .start()
        .then((segs) => {
          const items = segmentsToItems(segs, ++batchNoRef.current);
          if (!items.length || queueRef.current) return; // nothing to play, or the listener already picked something
          // Autoplay without a gesture is blocked: queue it paused.
          setNewQueue(
            items,
            radio.label(),
            makeSource('radio', b, { params: radio.params(), radio, exhausted: radio.exhausted }),
            0,
            { play: false }
          );
          setOpenPanel('queue');
          infoToast('Radio is ready. Press play to start.', 5000);
        })
        .catch((err) => errToast(errText(err, RADIO_ERROR)));
    }
  };

  // Preload the next queue item into the standby element. Idempotent — safe
  // to call from multiple triggers. Deliberately NOT called at track start:
  // a parallel download would compete with the track the user is waiting on.
  const preloadNext = () => {
    const cur = currentRef.current;
    const q = queueRef.current;
    if (!cur || !q) return;
    const next = q.items[q.index + 1];
    const sb = 1 - activeIdxRef.current;
    if (next && srcsRef.current[sb] !== next.url) {
      audiosRef.current[sb].src = next.url;
      srcsRef.current[sb] = next.url;
      standbyFailedRef.current = '';
      audiosRef.current[sb].load();
    }
  };

  const abortStandby = () => {
    const sb = 1 - activeIdxRef.current;
    if (srcsRef.current[sb]) {
      audiosRef.current[sb].removeAttribute('src');
      audiosRef.current[sb].load();
      srcsRef.current[sb] = '';
    }
    standbyFailedRef.current = '';
  };

  // A track that fails to load (archive.org 5xx or 404, a dead file) is
  // skipped with a toast (F5). A run of SKIP_CAP failures stops playback, so
  // a dead host can never spin the queue. Reads refs only: it is called from
  // the 'error' listener bound once at mount (D.2 invariant 3).
  const onMediaError = (el) => {
    const i = audiosRef.current.indexOf(el);
    const src = i >= 0 ? srcsRef.current[i] : '';
    const err = el.error;
    // Only a real failure of the url we assigned: a cleared or replaced src,
    // or an aborted fetch, is not one.
    if (!src || !err || err.code === 1 || el.getAttribute('src') !== src) return;
    if (i !== activeIdxRef.current) {
      standbyFailedRef.current = src;
      return;
    }
    const cur = currentRef.current;
    const q = queueRef.current;
    if (!cur || !q || cur.url !== src) return;
    // Paused (a link queued without a gesture): nothing to skip yet. Play
    // reloads the track (the playing effect), and a second failure skips.
    if (!playingRef.current) {
      setSongSpinner(false);
      return;
    }
    failActiveTrack(q);
  };

  // The active track failed (a load error, or the stall watchdog): skip it
  // with a toast, under the SKIP_CAP run of the current playIntent.
  const failActiveTrack = (q) => {
    const run = failRunRef.current;
    if (run.intent !== playIntentRef.current) {
      run.intent = playIntentRef.current;
      run.n = 0;
    }
    run.n += 1;
    const stop = (msg) => {
      batch(() => {
        setPlaying(false);
        setSongSpinner(false);
      });
      toast.dismiss(SKIP_TOAST_ID);
      errToast(msg);
    };
    if (run.n >= SKIP_CAP) {
      stop(SKIP_CAP + " tracks in a row couldn't load, so playback stopped. Press play to try again.");
      return;
    }
    const skipToast = () =>
      toast("Couldn't load this track, skipping", { type: toast.TYPE.INFO, autoClose: 2500, toastId: SKIP_TOAST_ID });
    if (stepTrack(1)) {
      skipToast();
      return;
    }
    // the failed track was the tail: wait for the next page (invariant 5)
    if (fetchingRef.current === q.gen || needsMore(q)) {
      skipToast();
      resumeAfterFetch(q);
      return;
    }
    stop("Couldn't load this track.");
  };

  // Stall watchdog (E4), every STALL_TICK_MS from a timer bound once at
  // mount, so it reads refs only. A server that holds the request open never
  // fires 'error': while the listener wants playback, an active element that
  // has not advanced, received bytes or seeked for STALL_MS, and has less
  // than HAVE_FUTURE_DATA, fails like a load error. A pause, a finished tail
  // waiting for its next page, an element with an error ('error' owns it) or
  // a track change resets the clock. The standby element is never checked:
  // its hung preload surfaces only if it is handed off to and then stalls.
  const onStallCheck = () => {
    const now = Date.now();
    const i = activeIdxRef.current;
    const el = audiosRef.current[i];
    const src = srcsRef.current[i];
    const cur = currentRef.current;
    const q = queueRef.current;
    const s = stallRef.current;
    const moved = s.el !== el || s.src !== src || s.t !== el.currentTime;
    const idle =
      !playingRef.current ||
      !cur ||
      !q ||
      !src ||
      cur.url !== src ||
      el.ended ||
      el.error ||
      el.readyState >= HAVE_FUTURE_DATA ||
      resumeOnAppendRef.current === q.gen ||
      stalledAt(i);
    if (moved || idle) {
      stallRef.current = { el, src, t: el.currentTime, since: now };
      return;
    }
    if (now - s.since < STALL_MS) return;
    stallRef.current = { el, src, t: el.currentTime, since: now };
    const r = stallRetryRef.current;
    const played = playedRef.current.el === el && playedRef.current.src === src;
    if (played && !(r && r.el === el && r.src === src)) {
      resumeStalled(el, src, el.currentTime);
      return;
    }
    stallMarkRef.current[i] = src;
    failActiveTrack(q);
  };

  // Reload a stalled, already playing track once and seek back to where it
  // stopped; if the reload stalls too, the next check fails the track.
  const resumeStalled = (el, src, pos) => {
    const retry = { el, src };
    stallRetryRef.current = retry;
    const onMeta = () => {
      el.removeEventListener('loadedmetadata', onMeta);
      // a new pick or a new url since the reload: leave its position alone
      if (stallRetryRef.current !== retry || srcsRef.current[audiosRef.current.indexOf(el)] !== src) return;
      try {
        el.currentTime = pos;
      } catch (e) {
        /* not seekable: it plays from the start */
      }
    };
    el.addEventListener('loadedmetadata', onMeta);
    el.load();
    if (!playingRef.current || el !== activeEl()) return;
    el.play().catch((err) => {
      if (playRejectHandled(err) || el !== activeEl()) return;
      setPlaying(false);
      setSongSpinner(false);
    });
  };

  // Audio engine wiring: DOM-attach the elements and the event handlers.
  // Bound once: everything below reads refs, never render-time state.
  useEffect(() => {
    const els = audiosRef.current;
    els.forEach((el) => {
      el.style.display = 'none';
      document.body.appendChild(el);
      el.addEventListener('playing', () => {
        if (el !== activeEl()) return;
        setSongSpinner(false);
        failRunRef.current.n = 0; // a track plays: the failure run is over
        playedRef.current = { el, src: srcsRef.current[activeIdxRef.current] };
      });
      el.addEventListener('error', () => onMediaError(el));
      // stall watchdog (E4): bytes arriving or a seek restart the clock
      el.addEventListener('progress', () => {
        if (el === activeEl()) stallTouch();
      });
      el.addEventListener('seeking', () => {
        if (el === activeEl()) stallTouch();
      });
      el.addEventListener('loadstart', () => {
        const i = audiosRef.current.indexOf(el);
        if (i >= 0) stallMarkRef.current[i] = '';
      });
      el.addEventListener('waiting', () => {
        if (el === activeEl()) setSongSpinner(true);
      });
      el.addEventListener('canplaythrough', () => {
        // current track is buffered enough to finish — now it's safe to
        // start warming the next one for the gapless hand-off
        if (el === activeEl() && currentRef.current) preloadNext();
      });
      el.addEventListener('ended', () => {
        if (el !== activeEl()) return;
        if (stepTrack(1)) return;
        const q = queueRef.current;
        if (q && (fetchingRef.current === q.gen || needsMore(q))) {
          resumeAfterFetch(q);
          return;
        }
        setPlaying(false);
      });
    });
    // the 100 ms progress ticker lives in PlayerProgress (onProgressTick)
    const stallTimer = setInterval(onStallCheck, STALL_TICK_MS);
    return () => clearInterval(stallTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track change: hot-swap to the preloaded standby element when the new
  // track is already buffered (gapless), otherwise load it into the active
  // element. Keyed on the item's qid and playNonce, never on object identity:
  // appends and re-renders must not re-run it, because the third branch
  // restarts the track (D.2 invariant 1).
  useEffect(() => {
    const cur = currentRef.current;
    if (!cur) return;
    const idx = activeIdxRef.current;
    const sbIdx = 1 - idx;
    stallTouch();
    stallRetryRef.current = null;
    playedRef.current = { el: null, src: '' };
    // never hand off to a standby whose preload failed (F5) or that stalled (E4)
    if (
      srcsRef.current[sbIdx] === cur.url &&
      standbyFailedRef.current !== cur.url &&
      !audiosRef.current[sbIdx].error &&
      !stalledAt(sbIdx)
    ) {
      audiosRef.current[idx].pause();
      activeIdxRef.current = sbIdx;
    } else if (srcsRef.current[idx] !== cur.url) {
      // kill any in-flight standby download so the whole pipe belongs to
      // the track the user just asked for
      abortStandby();
      audiosRef.current[idx].src = cur.url;
      srcsRef.current[idx] = cur.url;
      audiosRef.current[idx].load();
    } else if (audiosRef.current[idx].error || stalledAt(idx)) {
      audiosRef.current[idx].load(); // the same url failed or stalled before: try it again
    } else {
      audiosRef.current[idx].currentTime = 0;
    }
    const el = activeEl();
    el.volume = volumeRef.current;
    if (playingRef.current) {
      el.play().catch((err) => {
        // autoplay blocked (deep link before any user gesture) — show ▶.
        // An AbortError only means a newer load or pause superseded this
        // play() (fast track changes); it must not pause the new track.
        // A NotSupportedError is a failed load: the 'error' listener skips.
        if (playRejectHandled(err) || el !== activeEl()) return;
        setPlaying(false);
        setSongSpinner(false);
      });
    }
    // next-track preload is armed by canplaythrough / the 15s ticker rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQid, playNonce]);

  // Fetch-more: under 45 minutes left (or the first fill), the source can fetch, nothing in flight.
  useEffect(() => {
    if (queue && needsMore(queue) && fetchingRef.current !== queue.gen) fetchMore(queue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  // Surprise Me excludes recently played shows (D.6; never rendered).
  useEffect(() => {
    const cur = currentRef.current;
    if (!cur || !cur.sid) return;
    const k = cur.bandKey + '|' + cur.sid;
    if (lastSidRef.current === k) return;
    lastSidRef.current = k;
    pushRecentSid(cur.bandKey, cur.sid);
  }, [currentQid]);

  useEffect(() => {
    const el = activeEl();
    if (playing && currentRef.current) {
      // Play on a track that failed to load or stalled (paused on it, or
      // stopped after the skip cap): load it again; a fresh failure run
      // starts (F5, E4).
      stallTouch();
      if ((el.error || stalledAt(activeIdxRef.current)) && srcsRef.current[activeIdxRef.current]) {
        failRunRef.current = { intent: playIntentRef.current, n: 0 };
        el.load();
      }
      el.play().catch((err) => {
        if (playRejectHandled(err) || el !== activeEl()) return;
        setPlaying(false);
        setSongSpinner(false);
      });
    } else {
      el.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  useEffect(() => {
    audiosRef.current.forEach((a) => {
      a.volume = volume;
    });
  }, [volume]);

  useEffect(() => {
    const url = new URLSearchParams(window.location.search).get('set');
    if (url) {
      const route = url.replace(/.*?:\/\//g, '').replace(/%20/g, ' ');
      const parts = route.split('~');
      if (parts.length >= 5) linkSet(parts);
    }
    // x= links (D.5) only when there is no set= link
    const x = url ? null : parseX(window.location.search);
    const xBand = x ? bandByKey(x.data.b) : null;
    loadBand(xBand || BANDS[0]);
    if (x && xBand) openXLink(x, xBand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll the Setlist column to the playing track after openShow.
  useEffect(() => {
    if (!browseSet || !scrollToPlayingRef.current) return;
    scrollToPlayingRef.current = false;
    const box = setlistScrollRef.current;
    const row = box && box.querySelector('.track-row.playing');
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'center' });
  }, [browseSet]);

  // ...and the Shows column to the opened show.
  useEffect(() => {
    if (!selShow || !scrollToShowRef.current) return;
    scrollToShowRef.current = false;
    const box = showsScrollRef.current;
    const row = box && box.querySelector('.show-row.selected, .ss-row.selected');
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [selShow]);

  // Keyboard, page-wide (window, so every control and popover sees the key
  // first). Escape closes the open overlay panel. Space toggles playback and
  // the arrows scrub 10 s, but only when no control owns the key: a key a
  // control already handled (defaultPrevented), or one aimed at a focused
  // button, link, tab, radio, switch, slider, popover or open panel, is left
  // to that control.
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target && e.target.nodeType === 1 ? e.target : null;
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (t && t.closest && t.closest('.ss-pop, .vb-pop')) return;
        if (openPanelRef.current) {
          e.preventDefault();
          setOpenPanel(null);
          return;
        }
        // narrow drill-down: Escape is Back (not from a text field)
        if (paneRef.current && paneRef.current !== 'shows' && !(t && t.closest && t.closest('input, textarea, select, .hdr-menu'))) {
          e.preventDefault();
          paneBack();
        }
        return;
      }
      if (e.code !== 'Space' && e.code !== 'ArrowLeft' && e.code !== 'ArrowRight') return;
      if (t) {
        if (t.isContentEditable || (t.closest && t.closest(OWN_KEYS))) return;
        // inside an open panel, but not the panel itself (a freshly opened
        // panel holds focus; Space there still pauses)
        const panel = t.closest ? t.closest('.panel.open') : null;
        if (panel && panel !== t) return;
      }
      if (e.code === 'Space') {
        e.preventDefault();
        if (currentRef.current) setPlaying((p) => !p);
      } else if (currentRef.current) {
        e.preventDefault();
        if (e.code === 'ArrowLeft') fastBackward();
        else fastForward();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The radio panel focuses itself before App's effect below runs, so the
  // opener is taken from the last focus outside the panels.
  useEffect(() => {
    const onFocusIn = (e) => {
      const t = e.target;
      if (t && t.closest && !t.closest('.panel')) lastOutsideFocusRef.current = t;
    };
    document.addEventListener('focusin', onFocusIn, true);
    return () => document.removeEventListener('focusin', onFocusIn, true);
  }, []);

  // Overlay panels are dialogs: focus moves into a panel when it opens and
  // goes back to the element that opened it when it closes (if focus was in
  // the panel, or was lost to the page). The radio panel focuses itself.
  useEffect(() => {
    const prev = prevPanelRef.current;
    prevPanelRef.current = openPanel;
    if (prev === openPanel) return;
    const panelEl = (key) =>
      key === 'bands'
        ? sidebarRef.current
        : key === 'queue'
        ? queuePanelRef.current
        : key === 'search'
        ? searchPanelRef.current
        : key === 'song'
        ? songPanelRef.current
        : key === 'radio'
        ? document.querySelector('.vb-radio-panel')
        : null;
    const active = document.activeElement;
    const prevEl = panelEl(prev);
    if (openPanel) {
      if (!prev) {
        const inPanel = !!(active && active.closest && active.closest('.panel'));
        panelOpenerRef.current = !active || active === document.body ? null : inPanel ? lastOutsideFocusRef.current : active;
      }
      const el = panelEl(openPanel);
      if (el && openPanel !== 'radio' && !el.contains(active)) el.focus({ preventScroll: true });
      return;
    }
    const opener = panelOpenerRef.current;
    panelOpenerRef.current = null;
    const lost = !active || active === document.body || (prevEl && prevEl.contains(active));
    if (lost && opener && opener.focus && document.body.contains(opener)) opener.focus({ preventScroll: true });
  }, [openPanel]);

  // Narrow drill-down focus: a forward move focuses the new pane, Back
  // focuses the row selected there (or the pane). Panes the listener did not
  // move to (a tape arriving after Open show) leave focus alone.
  useEffect(() => {
    const dir = paneMoveRef.current;
    paneMoveRef.current = null;
    if (!pane || !dir) return;
    const el = paneElsRef.current[pane];
    if (!el) return;
    const row = dir === 'back' ? el.querySelector('.show-row.selected, .ss-row.selected, .tape-row.selected') : null;
    (row || el).focus({ preventScroll: true });
  }, [pane]);

  // Leaving the compact layouts closes the band drawer; leaving narrow
  // closes the header menu.
  useEffect(() => {
    if (!compact && openPanelRef.current === 'bands') setOpenPanel(null);
    if (!narrow) setMenuOpen(false);
  }, [compact, narrow]);

  // Header overflow menu: focus its first item on open; a click outside it
  // closes it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const first = menuRef.current && menuRef.current.querySelector('[role="menuitem"]');
    if (first) first.focus();
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && moreBtnRef.current && !moreBtnRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [menuOpen]);

  // OS media keys / lock-screen metadata, per queue item.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (current) {
      /* global MediaMetadata */
      navigator.mediaSession.metadata = new MediaMetadata({
        title: current.title,
        artist: current.bandName,
        album: fmtDate(current.dateKey) + ' — ' + current.venue,
      });
      navigator.mediaSession.setActionHandler('play', () => setPlaying(true));
      navigator.mediaSession.setActionHandler('pause', () => setPlaying(false));
      navigator.mediaSession.setActionHandler('previoustrack', prevTrack);
      navigator.mediaSession.setActionHandler('nexttrack', nextTrack);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    document.title = current ? current.title + ' — ' + current.bandName : 'DeadSpread';
  }, [current]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('ds-theme', theme);
    } catch (e) {
      /* private mode */
    }
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const closePanel = () => setOpenPanel(null);

  // Keyed on the items, not the queue: a track change (index) keeps the groups.
  const queueItems = queue ? queue.items : null;
  const groups = useMemo(() => (queueItems ? groupItems(queueItems) : []), [queueItems]);
  const queuedLeft = useMemo(() => (queue ? queuedSecs(queue.items, queue.index).secs : 0), [queue]);
  // Stable callbacks for the memo QueueList (they read refs at call time).
  const queuePickRef = useRef(null);
  queuePickRef.current = (index) => {
    playIntentRef.current += 1; // a pick: a pending async play must not override it
    playAt(queueRef.current, index);
  };
  const onQueuePick = useRef((index) => queuePickRef.current(index)).current;
  const queueOpenShowRef = useRef(null);
  queueOpenShowRef.current = onOpenShow;
  const onQueueOpenShow = useRef((t) => queueOpenShowRef.current(t)).current;

  if (isMobile) return <MobileGate />;

  const searchOn = !!(meta && meta.mode !== 'off');
  const radioFeatures = searchOn && meta.features && meta.features.radio ? meta.features.radio : null;
  const radioOn = !!(radioFeatures && Object.keys(radioFeatures).some((k) => radioFeatures[k]));
  const filteredEmpty = !!findApplied && !loading.shows && !findResult.error && shows.length === 0;
  const source = queue ? queue.source : null;
  const setLength = source && (source.kind === 'show' || source.kind === 'link') ? source.setLength : null;
  const showGroupHeads = !!queue && (groups.length > 1 || (source.kind !== 'show' && source.kind !== 'link'));
  const multiBand = groups.some((g) => g.bandKey !== (groups[0] && groups[0].bandKey));
  const shareTarget = current
    ? {
        bandKey: current.bandKey,
        id: current.tapeId,
        date: current.dateKey,
        venue: current.venue,
        location: current.location,
        bandName: current.bandName,
      }
    : null;
  const searchLink =
    source && source.kind === 'matches' && source.params ? buildFindLink(bandKeyOf(source.band), source.params) : undefined;
  const radioLink =
    source && source.kind === 'radio' && source.radio ? buildRadioLink(bandKeyOf(source.band), source.radio.params()) : undefined;
  const bestTape = bestTapeId ? tapes.find((t) => t.id === bestTapeId) : null;
  const sortedTapes = bestTape ? [bestTape].concat(tapes.filter((t) => t !== bestTape)) : tapes;
  const alignKeys = align && align.tapeId === selTapeId ? align.keys : null;

  // Bands that mix setlist shows with shows matched from tape titles
  // (coverage SPEC 7.3): say when a search leaves the tape-title shows out.
  const fillBand = isFillBand(meta);
  let fillNote = null;
  if (fillBand && findApplied) {
    const fp = api.toFindParams(findApplied, { withSort: false });
    if (fp.has('seq') || fp.has('slot') || fp.has('sandwich')) fillNote = 'Song order, opener and encore come from setlists, so From tape shows are left out.';
    else if (fp.has('not')) fillNote = 'From tape shows are left out unless every track was named.';
  }

  // Props for the search/ and vibe/ components (contract D.4).
  const mountProps = {
    activeFilterChips: { query: findApplied, onChange: onActiveFilterChange, onClear: clearFind, meta },
    setSearchPanel: {
      band,
      meta,
      catalog,
      value: findDraft,
      onChange: setFindDraft,
      onSubmit: (q) => runFind(q),
      onOpenSong,
    },
    findResultsHeader: {
      total: findResult.total,
      sort: findApplied ? findApplied.sort : 'date',
      sorts: meta && meta.features && Array.isArray(meta.features.sorts) ? meta.features.sorts : ['date', '-date'],
      onSort: onFindSort,
      onPlayMatches: () => {
        const q = findAppliedRef.current;
        const b = bandRef.current;
        const bg = bandGenRef.current;
        if (!q) return;
        const intent = claimPlayIntent();
        const stillWanted = () => bg === bandGenRef.current && intent === playIntentRef.current;
        api
          .findMatches(b, q, { limit: api.MATCHES_PAGE })
          .then((res) => {
            if (!stillWanted()) return;
            if (!res.segments.length) {
              errToast('None of these shows can be played right now.');
              return;
            }
            onPlaySegments(
              res.segments,
              { kind: 'matches', band: b, params: q, cursor: res.cursor, exhausted: !res.cursor },
              { title: 'Set Search', intent }
            );
          })
          .catch((err) => {
            if (stillWanted()) errToast(errText(err, FIND_ERROR));
          });
      },
      onShare: onShareFind,
      loading: findResult.loading,
      note: meta && Array.isArray(meta.notes) && meta.notes.length ? meta.notes[0] : null,
      mode: findApplied ? findApplied.mode : 'all',
      fillNote,
    },
    findResultRow: (row, part) => ({
      row,
      part,
      fillBand,
      selected: !!selShow && !!row.sid && selShow.sid === row.sid,
      onSelect: () => {
        selectShow(row);
        goPane('tapes');
      },
    }),
    countPill: {
      count: { n: 0, dropOne: findResult.dropOne || [] },
      loading: findResult.loading,
      onDrop: onDropOne,
      suggestionsOnly: true,
    },
    surpriseMeButton: { band, meta, onPlaySegments, onOpenShow, claimPlayIntent },
    showInsights: selShow
      ? {
          band,
          show: { sid: selShow.sid || null, dateKey: selShow.dateKey, iso: selShow.iso || isoFromDateKey(selShow.dateKey) },
          tapeId: selTapeId,
          fillBand,
          onOpenShow,
          onOpenSong,
          onPlaySegments,
          claimPlayIntent,
        }
      : null,
    vibeRadioPanel: { band, meta, open: openPanel === 'radio', onClose: closePanel, onStart: onStartRadio },
    radioThumbs: current && source && source.kind === 'radio' ? { item: current, onVote: onRadioVote } : null,
    vibeCheckBox: { band, meta, value: findDraft, onApply: onApplyQuery, catalog },
    songPage: songView
      ? {
          band: bandByKey(songView.bandKey) || band,
          songKey: songView.songKey,
          onClose: closePanel,
          onPlaySegments,
          onOpenShow,
          onOpenSong,
          onAddToSearch,
          claimPlayIntent,
        }
      : null,
  };

  // Header overflow menu (under 480 px): Radio and the theme toggle.
  const closeMenu = () => {
    setMenuOpen(false);
    if (moreBtnRef.current) moreBtnRef.current.focus();
  };
  const menuPick = (fn) => () => {
    closeMenu();
    fn();
  };
  const onMenuKey = (e) => {
    const items = Array.from(menuRef.current ? menuRef.current.querySelectorAll('[role="menuitem"]') : []);
    const i = items.indexOf(document.activeElement);
    const n = items.length;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeMenu();
    } else if (n && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      items[(i + (e.key === 'ArrowDown' ? 1 : -1) + n) % n].focus();
    } else if (n && (e.key === 'Home' || e.key === 'End')) {
      e.preventDefault();
      items[e.key === 'Home' ? 0 : n - 1].focus();
    } else if (e.key === 'Tab') {
      setMenuOpen(false);
    }
  };
  const themeTitle = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';

  // Narrow panes: the active one is shown (with an entry slide); the others
  // keep their layout and scroll position but are hidden and inert. Wide and
  // mid layouts get no extra classes or attributes.
  const PANE_LABEL = { shows: 'Shows', tapes: 'Recordings', set: 'Setlist' };
  const paneCls = (k) => (!pane ? '' : pane === k ? ' pane-on pane-in-' + paneAnimRef.current : ' pane-off');
  const paneAttrs = (k) => {
    const ref = paneRefCbsRef.current[k];
    if (!pane) return { ref };
    const off = pane !== k;
    return { ref, tabIndex: -1, 'aria-label': PANE_LABEL[k], 'aria-hidden': off ? true : undefined, inert: off ? '' : undefined };
  };

  const yearSelect = (
    <div className="year-select">
      <Select
        aria-label="Year"
        placeholder={findApplied ? 'Search results' : 'Year'}
        value={!findApplied && year ? { value: year, label: year } : null}
        options={years}
        onChange={changeYear}
        styles={findApplied ? yearSelectSearchStyles : selectStyles}
        isSearchable={false}
      />
    </div>
  );

  // Browse rows. Find rows are FindResultRow, keyed and selected by sid;
  // early and late shows on one date carry a badge (showPart).
  const findParts = findApplied ? partBadges(shows) : null;
  const renderShowRow = (show, i) => {
    if (findApplied) return <FindResultRow key={show.sid || 'd:' + show.dateKey} {...mountProps.findResultRow(show, findParts[i])} />;
    const selected = !!selShow && selShow.dateKey === show.dateKey;
    return (
      <button
        key={show.dateKey}
        className={'row show-row' + (selected ? ' selected' : '')}
        onClick={(e) => {
          releaseMouseFocus(e);
          selectShow(show);
          goPane('tapes');
        }}
      >
        <div className="row-main">
          <div className="row-title">{fmtDateLong(show.displayDate)}</div>
          <div className="row-sub">{show.venue}</div>
          <div className="row-sub dim">{show.location}</div>
        </div>
        <span className="badge mono">{show.length}</span>
      </button>
    );
  };

  return (
    <div className={'app' + (compact ? ' app-compact' : '') + (narrow ? ' app-narrow' : '')}>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar closeButton={false} />

      <div className="app-body">
        {/* ---------- sidebar ---------- */}
        {compact && (
          <div className={'panel-overlay' + (openPanel === 'bands' ? ' open' : '')} onClick={closePanel} aria-hidden="true" />
        )}
        <aside
          ref={sidebarRef}
          className={'sidebar' + (compact ? ' sidebar-drawer' + (openPanel === 'bands' ? ' open' : '') : '')}
          {...(compact
            ? {
                role: 'dialog',
                'aria-label': 'Bands',
                'aria-hidden': openPanel !== 'bands',
                inert: openPanel !== 'bands' ? '' : undefined,
                tabIndex: -1,
              }
            : {})}
        >
          <div className="sidebar-brand">
            <span className="brand-text">
              Dead<span>Spread</span>
            </span>
            {compact && (
              <button className="icon-btn" onClick={closePanel} title="Close" aria-label="Close band list">
                <IconClose size={18} />
              </button>
            )}
          </div>
          <div className="sidebar-label">Library</div>
          <nav className="band-list">
            {BANDS.map((b) => (
              <button
                key={b.key}
                className={'band-row' + (b.key === band.key ? ' active' : '')}
                onClick={() => {
                  if (openPanel === 'bands') setOpenPanel(null);
                  if (b.key === band.key) return;
                  pendingOpenRef.current = null; // the listener's own pick wins over a pending Open show
                  loadBand(b);
                }}
              >
                <span className="band-art">
                  <img src={b.logo} alt="" />
                </span>
                <span className="band-name">{b.name}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* ---------- main ---------- */}
        <main className="main">
          <header className="header">
            <div className="header-title-row">
              {compact && (
                <button
                  className={'icon-btn hdr-bands' + (openPanel === 'bands' ? ' active' : '')}
                  title="Bands"
                  aria-label="Band list"
                  aria-haspopup="dialog"
                  aria-expanded={openPanel === 'bands'}
                  onClick={() => setOpenPanel('bands')}
                >
                  <IconList size={18} />
                </button>
              )}
              <h1 className="header-title">{band.name}</h1>
              <div className="header-controls">
                {searchOn && (
                  <button
                    className={'icon-btn' + (openPanel === 'search' ? ' active' : '')}
                    title="Set Search"
                    onClick={() => setOpenPanel('search')}
                  >
                    <IconSearch size={17} />
                  </button>
                )}
                <SurpriseMeButton {...mountProps.surpriseMeButton} />
                {radioOn && (
                  <button
                    className={'icon-btn' + (narrow ? ' hdr-opt' : '') + (openPanel === 'radio' ? ' active' : '')}
                    title="Radio"
                    onClick={() => setOpenPanel('radio')}
                  >
                    <IconRadio size={17} />
                  </button>
                )}
                <button className={'icon-btn' + (narrow ? ' hdr-opt' : '')} title={themeTitle} onClick={toggleTheme}>
                  {theme === 'dark' ? <IconSun size={17} /> : <IconMoon size={17} />}
                </button>
                {!narrow && yearSelect}
                {narrow && (
                  <div className="hdr-more-wrap">
                    <button
                      ref={moreBtnRef}
                      className={'icon-btn hdr-more' + (menuOpen ? ' active' : '')}
                      title="More"
                      aria-label="More"
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      onClick={() => setMenuOpen((o) => !o)}
                    >
                      <IconMore size={18} />
                    </button>
                    {menuOpen && (
                      <div className="hdr-menu" role="menu" aria-label="More" ref={menuRef} onKeyDown={onMenuKey}>
                        {radioOn && (
                          <button role="menuitem" tabIndex={-1} data-title="Radio" onClick={menuPick(() => setOpenPanel('radio'))}>
                            <IconRadio size={16} />
                            <span>Radio</span>
                          </button>
                        )}
                        <button role="menuitem" tabIndex={-1} data-title="Theme" onClick={menuPick(toggleTheme)}>
                          {theme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
                          <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* its own full-width row, so the title and controls never get squeezed */}
            {findApplied && (
              <div className="header-filters">
                <ActiveFilterChips {...mountProps.activeFilterChips} />
              </div>
            )}
          </header>

          {pane && pane !== 'shows' && selShow && (
            <div className="pane-nav">
              <button className="pane-back" onClick={paneBack} aria-label={'Back to ' + PANE_BACK_LABEL[pane]}>
                <IconChevronLeft size={16} />
                <span>{PANE_BACK_LABEL[pane]}</span>
              </button>
              <div className="pane-ctx">
                <div className="pane-ctx-title">{fmtDateLong(selShow.iso || selShow.displayDate || selShow.dateKey)}</div>
                <div className="pane-ctx-sub">{[selShow.venue, selShow.location].filter(Boolean).join(' · ')}</div>
              </div>
            </div>
          )}

          <div className="columns">
            {/* shows */}
            <section className={'col' + paneCls('shows')} {...paneAttrs('shows')}>
              <div className="col-head">
                <span>Shows</span>
                {!loading.shows && !findResult.error && (
                  <span className="col-count">{findApplied ? fmtCount(findResult.total) : shows.length}</span>
                )}
                {narrow && yearSelect}
              </div>
              {findApplied && !findResult.error && <FindResultsHeader {...mountProps.findResultsHeader} />}
              <div className="col-scroll" ref={showsScrollRef}>
                {loading.shows ? (
                  <SkeletonRows />
                ) : findApplied && findResult.error ? (
                  <div className="inline-banner">
                    <span>{findResult.error}</span>
                    <button className="btn-pill" onClick={() => runFind(findApplied)}>
                      Try again
                    </button>
                  </div>
                ) : filteredEmpty ? (
                  <>
                    <EmptyState icon={<IconSearch size={26} />} title="No shows match all of these" hint="Try fewer songs or filters." />
                    {findResult.dropOne && findResult.dropOne.length > 0 && <CountPill {...mountProps.countPill} />}
                  </>
                ) : (
                  <>
                    {shows.map(renderShowRow)}
                    {findApplied && findResult.cursor && (
                      <button className="show-more-row" onClick={loadMoreFind} disabled={findResult.loadingMore}>
                        {findResult.loadingMore ? 'Loading' : 'Show more'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </section>

            {/* tapes */}
            <section className={'col' + paneCls('tapes')} {...paneAttrs('tapes')}>
              <div className="col-head">
                <span>Recordings</span>
                {!loading.tapes && tapes.length > 0 && <span className="col-count">{tapes.length}</span>}
              </div>
              <div className="col-scroll">
                {loading.tapes ? (
                  <SkeletonRows n={5} />
                ) : tapes.length === 0 ? (
                  <EmptyState icon={<IconMic size={26} />} title="Pick a show" hint="Recordings of the selected show appear here." />
                ) : (
                  sortedTapes.map((tape) => (
                    <button
                      key={tape.id}
                      className={'row tape-row' + (tape.id === selTapeId ? ' selected' : '')}
                      onClick={(e) => {
                        releaseMouseFocus(e);
                        selectTape(tape);
                        goPane('set');
                      }}
                    >
                      <div className="row-main">
                        <div className="tape-top">
                          {tape.id === bestTapeId && (
                            <span className="badge" title="The best recording of this show">
                              Best
                            </span>
                          )}
                          {tape.sbd && (
                            <span className="sbd-badge" title="Soundboard recording">
                              <IconMic size={11} /> SBD
                            </span>
                          )}
                          {band.wsp ? (
                            <span className="row-title">{tape.label}</span>
                          ) : tape.rating ? (
                            <span className="rating">
                              <IconStar size={12} /> {tape.rating}
                              <span className="dim">/10</span>
                            </span>
                          ) : (
                            <span className="row-sub dim">No rating</span>
                          )}
                          <span className="badge mono right">{tape.length}</span>
                        </div>
                        {!band.wsp && (
                          <>
                            <div className="row-sub">
                              {tape.downloads != null && (
                                <span className="meta-item">
                                  <IconArrowDown size={11} /> {fmtCount(tape.downloads)}
                                </span>
                              )}
                              {tape.taper && <span className="meta-item">Taper · {tape.taper}</span>}
                            </div>
                            {tape.source && (
                              <div className={'row-sub dim clamp' + (expandedTapes[tape.id] ? ' open' : '')}>{tape.source}</div>
                            )}
                            {tape.description && (
                              <div className={'row-sub dim clamp' + (expandedTapes[tape.id] ? ' open' : '')}>{tape.description}</div>
                            )}
                            {(tape.description || tape.source) && (
                              <span
                                className="more-link"
                                role="button"
                                tabIndex={-1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedTapes((x) => ({ ...x, [tape.id]: !x[tape.id] }));
                                }}
                              >
                                {expandedTapes[tape.id] ? 'Less' : 'More'}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </section>

            {/* setlist */}
            <section className={'col' + paneCls('set')} {...paneAttrs('set')}>
              <div className="col-head">
                <span>Setlist</span>
                {!loading.set && browseSet && <span className="col-count">{browseSet.tracks.length}</span>}
              </div>
              <div className="col-scroll" ref={setlistScrollRef}>
                {mountProps.showInsights && <ShowInsights {...mountProps.showInsights} />}
                {loading.set ? (
                  <SkeletonRows n={10} />
                ) : !browseSet ? (
                  <EmptyState icon={<IconNote size={26} />} title="Pick a recording" hint="Songs appear here — click one to play." />
                ) : (
                  browseSet.tracks.map((track, idx) => {
                    const isCurrent =
                      !!current && current.bandKey === band.key && current.tapeId === selTapeId && current.trackIdx === idx;
                    const keys = alignKeys ? alignKeys[idx] || [] : null;
                    const isMatch = !!(keys && matchKeys && keys.some((k) => matchKeys[k]));
                    const songKey = keys && keys.length && songByKey[keys[0]] ? keys[0] : null;
                    // A container with separate buttons (play, song page, add).
                    // A click anywhere else on the row still plays; the row
                    // itself takes no focus, so Space keeps meaning pause.
                    return (
                      <div
                        key={selTapeId + ':' + idx}
                        className={'row track-row' + (isCurrent ? ' playing' : '') + (isMatch ? ' match' : '')}
                        onClick={() => playFromBrowse(idx)}
                      >
                        <button
                          type="button"
                          className="track-play"
                          aria-label={'Play ' + track.no + ', ' + track.title}
                          onClick={(e) => {
                            e.stopPropagation();
                            releaseMouseFocus(e);
                            playFromBrowse(idx);
                          }}
                        >
                          <span className="track-no mono">{isCurrent && playing ? <Eq /> : track.no}</span>
                        </button>
                        <span className="track-title">
                          {songKey ? (
                            <button
                              type="button"
                              className="track-song"
                              title={'About ' + songName(songKey)}
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenSong(songKey);
                              }}
                            >
                              {track.title}
                            </button>
                          ) : (
                            track.title
                          )}
                        </span>
                        {keys && searchOn && (
                          <button
                            type="button"
                            className="track-add"
                            aria-disabled={keys.length ? 'false' : 'true'}
                            aria-label={keys.length ? 'Add ' + joinNames(keys.map(songName)) + ' to Set Search' : 'Song not recognized'}
                            title={keys.length ? 'Add to Set Search' : 'Song not recognized'}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (keys.length) addToSearch(keys.map((k) => ({ key: k, name: songName(k) })));
                            }}
                          >
                            <IconPlusCircle size={16} />
                          </button>
                        )}
                        <span className="badge mono">{track.length}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* ---------- player bar ---------- */}
      <footer className="player">
        <div className="player-now">
          {current ? (
            <>
              <span className="player-art">
                <img src={current.logo} alt="" />
              </span>
              <div className="player-meta">
                <div className="player-song">{current.no + ' - ' + current.title}</div>
                <div className="player-sub">
                  {current.bandName} · {fmtDate(current.dateKey)}
                  {current.venue ? ' · ' + current.venue : ''}
                </div>
              </div>
              {!narrow && (current.sid || current.dateKey) && (
                <button className="t-btn" title="Open full show" onClick={openCurrentShow}>
                  <IconOpen size={18} />
                </button>
              )}
              {!narrow && mountProps.radioThumbs && <RadioThumbs {...mountProps.radioThumbs} />}
            </>
          ) : (
            <>
              <span className="player-art empty">
                <IconNote size={20} />
              </span>
              <div className="player-meta">
                <div className="player-song dim">Not Playing</div>
                <div className="player-sub">Pick a show to get started</div>
              </div>
            </>
          )}
        </div>

        <div className="player-center">
          <div className="transport">
            <button
              className={'t-btn' + (narrow ? ' t-skip' : '')}
              title="Back 10 seconds"
              onClick={(e) => {
                releaseMouseFocus(e);
                fastBackward();
              }}
              disabled={!current}
            >
              <IconBack10 size={18} />
            </button>
            <button
              className={'t-btn' + (narrow ? ' t-skip' : '')}
              title="Previous track"
              onClick={(e) => {
                releaseMouseFocus(e);
                prevTrack();
              }}
              disabled={!current}
            >
              <IconPrev size={20} />
            </button>
            <button
              className="t-play"
              title={playing ? 'Pause' : 'Play'}
              onClick={(e) => {
                releaseMouseFocus(e);
                if (current) setPlaying(!playing);
              }}
              disabled={!current}
            >
              {songSpinner && playing ? (
                <span className="spinner" />
              ) : playing ? (
                <IconPause size={22} />
              ) : (
                <IconPlay size={22} />
              )}
            </button>
            <button
              className="t-btn"
              title="Next track"
              onClick={(e) => {
                releaseMouseFocus(e);
                nextTrack();
              }}
              disabled={!current}
            >
              <IconNext size={20} />
            </button>
            <button
              className={'t-btn' + (narrow ? ' t-skip' : '')}
              title="Forward 10 seconds"
              onClick={(e) => {
                releaseMouseFocus(e);
                fastForward();
              }}
              disabled={!current}
            >
              <IconFwd10 size={18} />
            </button>
          </div>
          <PlayerProgress
            getEl={getActiveElRef.current}
            active={!!current}
            length={current ? current.length : '00:00'}
            resetKey={(currentQid || '') + ':' + playNonce}
            onTick={progressTickRef.current}
          />
        </div>

        <div className="player-right">
          {!narrow && (
            <div className="volume">
              <IconVolume size={16} level={volume} />
              <div className="volume-slider">
                <Slider min={0} max={100} value={Math.round(volume * 100)} onChange={(v) => setVolume(v / 100)} />
              </div>
            </div>
          )}
          <button
            className={'icon-btn' + (openPanel === 'queue' ? ' active' : '')}
            title="Now Playing"
            onClick={() => setOpenPanel((p) => (p === 'queue' ? null : 'queue'))}
          >
            <IconQueue size={18} />
          </button>
        </div>
      </footer>

      {/* ---------- now playing panel ---------- */}
      <div className={'panel-overlay' + (openPanel === 'queue' ? ' open' : '')} onClick={closePanel} aria-hidden="true" />
      <div
        ref={queuePanelRef}
        className={'panel panel-right queue-panel' + (openPanel === 'queue' ? ' open' : '')}
        role="dialog"
        aria-label="Now Playing"
        aria-hidden={openPanel !== 'queue'}
        inert={openPanel !== 'queue' ? '' : undefined}
        tabIndex={-1}
      >
        <div className="panel-head">
          <div>
            <div className="panel-title">{current ? current.bandName : 'Now Playing'}</div>
            {current && (
              <div className="panel-sub">
                {fmtDateLong(current.dateKey)}
                {setLength ? ' · ' + setLength : ''}
                <br />
                <span className="dim">
                  {current.venue} — {current.location}
                </span>
                {queue.title && source.kind !== 'show' && source.kind !== 'link' && (
                  <>
                    <br />
                    <span className="dim">{queue.title}</span>
                  </>
                )}
                {source.kind !== 'show' && source.kind !== 'link' && queuedLeft > 0 && (
                  <>
                    <br />
                    <span className="dim queue-length" title="Music left in the queue, from the current track">
                      {fmtQueued(queuedLeft)} queued
                      {queueFetching === queue.gen && (
                        <span className="spinner" style={QUEUE_SPINNER_STYLE} role="status" aria-label="Queuing more" />
                      )}
                    </span>
                  </>
                )}
              </div>
            )}
            {!current && <div className="panel-sub dim">No show selected</div>}
          </div>
          <button className="icon-btn" onClick={closePanel} title="Close">
            <IconClose size={18} />
          </button>
        </div>
        {narrow && (
          <div className="np-controls">
            <div className="np-transport">
              <button className="t-btn" title="Back 10 seconds" onClick={fastBackward} disabled={!current}>
                <IconBack10 size={18} />
              </button>
              <button className="t-btn" title="Previous track" onClick={prevTrack} disabled={!current}>
                <IconPrev size={20} />
              </button>
              {/* the panel covers the compact bar, so pause lives here too */}
              <button
                className="t-play"
                title={playing ? 'Pause' : 'Play'}
                onClick={() => {
                  if (current) setPlaying(!playing);
                }}
                disabled={!current}
              >
                {songSpinner && playing ? (
                  <span className="spinner" />
                ) : playing ? (
                  <IconPause size={20} />
                ) : (
                  <IconPlay size={20} />
                )}
              </button>
              <button className="t-btn" title="Next track" onClick={nextTrack} disabled={!current}>
                <IconNext size={20} />
              </button>
              <button className="t-btn" title="Forward 10 seconds" onClick={fastForward} disabled={!current}>
                <IconFwd10 size={18} />
              </button>
              {current && (current.sid || current.dateKey) && (
                <button className="t-btn" title="Open full show" onClick={openCurrentShow}>
                  <IconOpen size={18} />
                </button>
              )}
              {mountProps.radioThumbs && <RadioThumbs {...mountProps.radioThumbs} />}
            </div>
            <div className="volume">
              <IconVolume size={16} level={volume} />
              <div className="volume-slider">
                <Slider min={0} max={100} value={Math.round(volume * 100)} onChange={(v) => setVolume(v / 100)} />
              </div>
            </div>
          </div>
        )}
        {shareTarget && (
          <div className="share-row">
            <div className="share-links">
              <CopyToClipboard
                text={shareUrl(shareTarget)}
                onCopy={() => toast('Show link copied to clipboard', { type: toast.TYPE.INFO, autoClose: 2000 })}
              >
                <button className="share-link" title="Copy show link" aria-label="Copy show link">
                  <IconLink size={14} />
                  <span>Show link</span>
                </button>
              </CopyToClipboard>
              {searchLink !== undefined &&
                (searchLink ? (
                  <CopyToClipboard text={searchLink} onCopy={() => infoToast('Search link copied', 2000)}>
                    <button className="share-link" title="Copy search link" aria-label="Copy search link">
                      <IconLink size={14} />
                      <span>Search link</span>
                    </button>
                  </CopyToClipboard>
                ) : (
                  <button
                    className="share-link"
                    title="Copy search link"
                    aria-label="Copy search link"
                    onClick={() => errToast(LINK_ERROR)}
                  >
                    <IconLink size={14} />
                    <span>Search link</span>
                  </button>
                ))}
              {radioLink !== undefined &&
                (radioLink ? (
                  <CopyToClipboard text={radioLink} onCopy={() => infoToast('Radio link copied', 2000)}>
                    <button className="share-link" title="Copy radio link" aria-label="Copy radio link">
                      <IconLink size={14} />
                      <span>Radio link</span>
                    </button>
                  </CopyToClipboard>
                ) : (
                  <button
                    className="share-link"
                    title="Copy radio link"
                    aria-label="Copy radio link"
                    onClick={() => errToast('This station cannot be shared as a link.')}
                  >
                    <IconLink size={14} />
                    <span>Radio link</span>
                  </button>
                ))}
            </div>
            <div className="share-social">
              <EmailShareButton url={shareUrl(shareTarget)}>
                <EmailIcon size={28} round />
              </EmailShareButton>
              <FacebookShareButton url={shareUrl(shareTarget)}>
                <FacebookIcon size={28} round />
              </FacebookShareButton>
              <PinterestShareButton url={shareUrl(shareTarget)} media={shareUrl(shareTarget)}>
                <PinterestIcon size={28} round />
              </PinterestShareButton>
              <TwitterShareButton url={shareUrl(shareTarget)}>
                <TwitterIcon size={28} round />
              </TwitterShareButton>
            </div>
          </div>
        )}
        <div className="panel-body">
          {!queue ? (
            <EmptyState icon={<IconNote size={26} />} title="Songs will appear here" hint="Select a show." />
          ) : (
            <QueueList
              groups={groups}
              index={queue.index}
              playing={playing}
              showHeads={showGroupHeads}
              multiBand={multiBand}
              onPick={onQueuePick}
              onOpenShow={onQueueOpenShow}
            />
          )}
        </div>
      </div>

      {/* ---------- Set Search panel ---------- */}
      <div className={'panel-overlay' + (openPanel === 'search' ? ' open' : '')} onClick={closePanel} aria-hidden="true" />
      <div
        ref={searchPanelRef}
        className={'panel panel-left' + (openPanel === 'search' ? ' open' : '')}
        role="dialog"
        aria-label="Set Search"
        aria-hidden={openPanel !== 'search'}
        inert={openPanel !== 'search' ? '' : undefined}
        tabIndex={-1}
      >
        <div className="panel-head">
          <div>
            <div className="panel-title">Set Search</div>
            <div className="panel-sub dim">Shows with every song you pick</div>
          </div>
          <button className="icon-btn" onClick={closePanel} title="Close">
            <IconClose size={18} />
          </button>
        </div>
        <div className="panel-body search-body">
          <VibeCheckBox {...mountProps.vibeCheckBox} />
          <SetSearchPanel {...mountProps.setSearchPanel} />
        </div>
      </div>

      {/* ---------- song page panel ---------- */}
      <div className={'panel-overlay' + (openPanel === 'song' ? ' open' : '')} onClick={closePanel} aria-hidden="true" />
      <div
        ref={songPanelRef}
        className={'panel panel-right panel-wide' + (openPanel === 'song' ? ' open' : '')}
        role="dialog"
        aria-label="Song page"
        aria-hidden={openPanel !== 'song'}
        inert={openPanel !== 'song' ? '' : undefined}
        tabIndex={-1}
      >
        {mountProps.songPage && <SongPage {...mountProps.songPage} />}
      </div>

      {/* ---------- radio station panel ---------- */}
      <VibeRadioPanel {...mountProps.vibeRadioPanel} />
    </div>
  );
}
